function fmtIsoDateLocal(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function applyChoiceFilter(params, filterDef, inKey, notInKey) {
  if (!filterDef || !Array.isArray(filterDef.values)) return;
  const values = filterDef.values.filter(Boolean);
  if (values.length === 0) return;
  if (filterDef.op === "is") {
    params[inKey] = values.join(",");
  } else if (filterDef.op === "is_not") {
    params[notInKey] = values.join(",");
  }
}

function applyDateRangeFilter(params, filterDef, fromKey, toKey) {
  if (!filterDef) return;
  if (filterDef.from) params[fromKey] = filterDef.from;
  if (filterDef.to) params[toKey] = filterDef.to;
}

// Shared by the Dashboard tab's own created_from/created_to window, the
// Lifecycle-bar status-counts fetch (activity_from/activity_to), AND
// handleLcSelect's click-through window (DefectsViewV2Page.jsx) — one date
// -math implementation instead of three separately-maintained copies of
// "days -> {from, to}" that could silently drift apart from each other.
export function computeRangeDates(range, customRange) {
  if (range === "custom" && customRange?.from && customRange?.to) {
    return { from: customRange.from, to: customRange.to };
  }
  const days = parseInt(range, 10) || 30;
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - (days - 1));
  return { from: fmtIsoDateLocal(from), to: fmtIsoDateLocal(to) };
}

function applyTeamFilters(params, teamFilters = {}) {
  const applyOne = (idsKey, opKey, targetIdsKey, targetOpKey) => {
    const selected = teamFilters?.[idsKey];
    if (!Array.isArray(selected) || selected.length === 0) return;
    params[targetIdsKey] = selected.join(",");
    params[targetOpKey] = (teamFilters?.[opKey] || "is");
  };

  applyOne("reportedTeamIds", "reportedTeamOp", "reported_team_ids", "reported_team_op");
  applyOne("owningTeamIds", "owningTeamOp", "owning_team_ids", "owning_team_op");
}

export function buildDefectsQueryParams({ viewTab, range, customRange, userId, teamFilters, appliedFilters, search }) {
  if (viewTab !== "dashboard" && viewTab !== "issues" && viewTab !== "mine" && viewTab !== "external") return null;

  const params = { view: "all" };

  if (viewTab === "dashboard") {
    const { from, to } = computeRangeDates(range, customRange);
    params.created_from = from;
    params.created_to = to;
    return { mode: "dashboard", params };
  }

  // Derive server-side status filter from appliedFilters.status.
  // op=is_not → status_not_in (server excludes listed statuses).
  // op=is     → status_in (server includes only listed statuses).
  // no filter → default exclusion keeps the result set to active defects.
  const statusFilter = appliedFilters?.status;
  const statusValues = Array.isArray(statusFilter?.values) ? statusFilter.values.filter(Boolean) : [];
  if (statusValues.length > 0 && statusFilter.op === "is_not") {
    params.status_not_in = statusValues.join(",");
  } else if (statusValues.length > 0 && statusFilter.op === "is") {
    params.status_in = statusValues.join(",");
  }

  if (viewTab === "mine" && userId) {
    params.assigned_to = userId;
  }
  if (viewTab === "external") {
    params.view = "external";
  }
  applyTeamFilters(params, teamFilters);
  applyChoiceFilter(params, appliedFilters?.severity,         "severity_in",          "severity_not_in");
  applyChoiceFilter(params, appliedFilters?.source,           "source_in",            "source_not_in");
  applyChoiceFilter(params, appliedFilters?.reporting_channel, "reporting_channel_in", "reporting_channel_not_in");
  applyChoiceFilter(params, appliedFilters?.priority,         "priority_in",          "priority_not_in");
  applyChoiceFilter(params, appliedFilters?.environment,      "environment_in",       "environment_not_in");
  applyChoiceFilter(params, appliedFilters?.defect_type,      "defect_type_in",       "defect_type_not_in");
  applyChoiceFilter(params, appliedFilters?.reported_by_name, "reporter_name_in",     "reporter_name_not_in");
  applyChoiceFilter(params, appliedFilters?.assigned_to_name, "owner_name_in",        "owner_name_not_in");
  applyChoiceFilter(params, appliedFilters?.customer_name,    "customer_name_in",     "customer_name_not_in");
  applyChoiceFilter(params, appliedFilters?.project_name,     "project_name_in",      "project_name_not_in");
  applyChoiceFilter(params, appliedFilters?.visibility,       "visibility_in",        "visibility_not_in");
  applyChoiceFilter(params, appliedFilters?.tags,             "tag_names_in",         "tag_names_not_in");
  applyDateRangeFilter(params, appliedFilters?.created_at, "created_from", "created_to");
  applyDateRangeFilter(params, appliedFilters?.updated_at, "updated_from", "updated_to");
  applyDateRangeFilter(params, appliedFilters?.due_date,   "due_from",     "due_to");
  applyDateRangeFilter(params, appliedFilters?.closed_at,  "closed_from",  "closed_to");
  // Set only when arriving via a Lifecycle-bar segment click (handleLcSelect,
  // DefectsViewV2Page.jsx) — a DELIBERATELY separate key from created_at
  // above. Matches created_at OR closed_at in range (see GET /defects's own
  // activity_from/activity_to docstring in routers/defects.py), the same
  // broadened window GET /defects/status-counts uses for the bar's own
  // count, so navigating in reproduces the exact number that was clicked.
  applyDateRangeFilter(params, appliedFilters?.activity_window, "activity_from", "activity_to");
  // Server-side counterpart to the Issues-table search box — see
  // GET /defects's own `search` param docstring (routers/defects.py) for why
  // this is needed: the list endpoint's default limit=500 (ORDER BY
  // updated_at DESC) means a purely client-side search over `defects` can
  // silently miss matches outside the fetched page for any tab/scope whose
  // total exceeds that cap. Sent debounced (DefectsViewV2Page.jsx), not on
  // every keystroke.
  if (search && search.trim()) params.search = search.trim();
  return { mode: "list", params };
}

// Params for GET /defects/status-counts — backs the Dashboard Lifecycle bar
// and its "N Open · M critical" header KPI. Uses the SAME date window
// (computeRangeDates) as the dashboard-mode branch above, sent as
// activity_from/activity_to (created OR closed in range) rather than
// created_from/created_to, matching what a Lifecycle-segment click sends
// when it navigates into the Issues tab (see applyDateRangeFilter's
// activity_window handling above) — this is what keeps the two numbers
// from drifting.
export function buildStatusCountsParams({ range, customRange }) {
  const { from, to } = computeRangeDates(range, customRange);
  return { view: "all", activity_from: from, activity_to: to };
}

export function buildDefectCountParams({ tabKey, userId, teamFilters, appliedFilters }) {
  if (tabKey !== "issues" && tabKey !== "mine" && tabKey !== "external") return null;
  if (tabKey === "mine" && !userId) return null;

  const params = { view: "all" };

  const statusFilter = appliedFilters?.status;
  const statusValues = Array.isArray(statusFilter?.values) ? statusFilter.values.filter(Boolean) : [];
  if (statusValues.length > 0 && statusFilter.op === "is_not") {
    params.status_not_in = statusValues.join(",");
  } else if (statusValues.length > 0 && statusFilter.op === "is") {
    params.status_in = statusValues.join(",");
  }

  if (tabKey === "mine" && userId) {
    params.assigned_to = userId;
  }
  if (tabKey === "external") {
    params.view = "external";
  }
  applyTeamFilters(params, teamFilters);
  applyChoiceFilter(params, appliedFilters?.severity,         "severity_in",          "severity_not_in");
  applyChoiceFilter(params, appliedFilters?.source,           "source_in",            "source_not_in");
  applyChoiceFilter(params, appliedFilters?.reporting_channel, "reporting_channel_in", "reporting_channel_not_in");
  applyChoiceFilter(params, appliedFilters?.priority,         "priority_in",          "priority_not_in");
  applyChoiceFilter(params, appliedFilters?.environment,      "environment_in",       "environment_not_in");
  applyChoiceFilter(params, appliedFilters?.defect_type,      "defect_type_in",       "defect_type_not_in");
  applyChoiceFilter(params, appliedFilters?.reported_by_name, "reporter_name_in",     "reporter_name_not_in");
  applyChoiceFilter(params, appliedFilters?.assigned_to_name, "owner_name_in",        "owner_name_not_in");
  applyChoiceFilter(params, appliedFilters?.customer_name,    "customer_name_in",     "customer_name_not_in");
  applyChoiceFilter(params, appliedFilters?.project_name,     "project_name_in",      "project_name_not_in");
  applyChoiceFilter(params, appliedFilters?.visibility,       "visibility_in",        "visibility_not_in");
  applyChoiceFilter(params, appliedFilters?.tags,             "tag_names_in",         "tag_names_not_in");
  applyDateRangeFilter(params, appliedFilters?.created_at, "created_from", "created_to");
  applyDateRangeFilter(params, appliedFilters?.updated_at, "updated_from", "updated_to");
  applyDateRangeFilter(params, appliedFilters?.due_date,   "due_from",     "due_to");
  applyDateRangeFilter(params, appliedFilters?.closed_at,  "closed_from",  "closed_to");
  return params;
}
